import { useEffect } from 'react';
import { useAssignableGroups, useSectionsList } from '../../school_panel/api/hooks';
import { useTaughtSubjects } from '../../school_panel/lib/teaching';
import IndividualStudentPicker from './IndividualStudentPicker';
import { Info } from 'lucide-react';
import { Segmented } from '../../school_panel/components/ui';
import { fieldClass } from '../../school_panel/lib/fieldClass';
import OverflowRow from '../../school_panel/components/ui/OverflowRow';
import { withTime } from '../../school_panel/lib/dates';

export type AssignMode = 'grade' | 'sections' | 'students';
export type ResultsRelease = 'immediate' | 'after_due' | 'manual';
export interface AssignValue {
  mode: AssignMode; sectionIds: number[]; studentIds: number[];
  dueDate: string; dueTime: string; instructions: string;
  resultsRelease: ResultsRelease;
}
export const EMPTY_ASSIGN: AssignValue = { mode: 'grade', sectionIds: [], studentIds: [], dueDate: '', dueTime: '', instructions: '', resultsRelease: 'after_due' };
const MAX = 2000;
const pad = (n: number) => String(n).padStart(2, '0');

function localIso(date: string, time: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = (time || '23:59').split(':').map(Number);
  const dt = new Date(y, m - 1, d, hh, mm);
  const off = -dt.getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  return `${date}T${pad(hh)}:${pad(mm)}:00${sign}${pad(Math.floor(Math.abs(off) / 60))}:${pad(Math.abs(off) % 60)}`;
}

export function assignFromExam(e: { assignment_scope?: string | null; assigned_section_ids?: number[]; assigned_student_ids?: number[]; due_at?: string | null; due_date?: string | null; instructions?: string | null; results_release?: string | null }): AssignValue {
  let dueDate = '', dueTime = '';
  if (e.due_at) {
    const d = new Date(e.due_at);
    dueDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    dueTime = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } else if (e.due_date) {
    dueDate = e.due_date; dueTime = '23:59';
  }
  const sections = e.assigned_section_ids ?? [];
  const mode: AssignMode = e.assignment_scope === 'individual' ? 'students' : sections.length ? 'sections' : 'grade';
  return { mode, sectionIds: sections, studentIds: e.assigned_student_ids ?? [], dueDate, dueTime, instructions: e.instructions ?? '',
    resultsRelease: e.results_release === 'immediate' || e.results_release === 'manual' ? e.results_release : 'after_due' };
}

export function assignPayload(v: AssignValue, currentScope?: string | null) {
  const gradeScope = currentScope === 'grade' || currentScope === 'grade_subject' ? currentScope : 'grade_subject';
  return {
    assignment_scope: v.mode === 'students' ? 'individual' : gradeScope,
    assigned_section_ids: v.mode === 'sections' ? v.sectionIds : [],
    assigned_student_ids: v.mode === 'students' ? v.studentIds : [],
    due_at: v.dueDate ? localIso(v.dueDate, v.dueTime) : null,
    instructions: v.instructions.trim(),
    results_release: v.resultsRelease,
  };
}

export function assignError(v: AssignValue): string | null {
  if (v.mode === 'sections' && v.sectionIds.length === 0) return 'Pick at least one section.';
  if (v.mode === 'students' && v.studentIds.length === 0) return 'Pick at least one student.';
  return null;
}

// Design `.opt` / `.opt.on`: option pills, navy fill when chosen (checkbox/radio kept, hidden, for keyboard and label semantics).
const pill = (on: boolean) =>
  `inline-flex min-h-[36px] cursor-pointer items-center gap-1.5 rounded-full border px-[13px] text-[13px] font-medium transition-colors focus-within:ring-2 focus-within:ring-accent ${
    on ? 'border-navy bg-navy text-white' : 'border-line-strong bg-surface text-ink hover:border-slate'}`;
const labelClass = 'mb-1.5 block text-[13px] font-semibold text-ink';
const inputClass = fieldClass;
const RELEASE_OPTIONS: Array<[ResultsRelease, string]> = [
  ['immediate', 'Right away'], ['after_due', 'After the due time'], ['manual', 'When I release them'],
];

/** Lower-case release wording for sentences ("after the due time"). */
export const releaseText = (r: ResultsRelease) => (RELEASE_OPTIONS.find(([k]) => k === r)?.[1] ?? '').toLowerCase();

/** "due Fri, 17 Oct, 09:00" or "no due date". */
export const dueText = (v: AssignValue) => v.dueDate
  ? `due ${withTime(localIso(v.dueDate, v.dueTime), { weekday: 'short', day: 'numeric', month: 'short' })}`
  : 'no due date';

/** Who the exam goes to: "All of Grade 10", "Grade 10 · Sections A, B", "3 students". */
export function audienceText(v: AssignValue, sections: Array<{ id: number; name: string }>, gradeLabel?: string): string {
  if (v.mode === 'students') return `${v.studentIds.length} student${v.studentIds.length === 1 ? '' : 's'}`;
  if (v.mode === 'sections') {
    const picked = sections.filter((s) => v.sectionIds.includes(s.id));
    return picked.length ? `${gradeLabel ? `${gradeLabel} · ` : ''}Sections ${picked.map((s) => s.name).join(', ')}` : 'No sections';
  }
  return gradeLabel ? `All of ${gradeLabel}` : 'Whole grade';
}
const errClass = 'mt-1.5 text-xs text-danger';

export default function AssignFields({ gradeId, gradeLabel, subjectId, kind, value, onChange, errors }: {
  gradeId: number | null; value: AssignValue; onChange: (v: AssignValue) => void; errors?: Record<string, string>;
  /** Display name of the grade ("Grade 10"), used in the section label and the summary. */
  gradeLabel?: string;
  /** The work's subject: a teacher gives it only to the sections they teach it in. */
  subjectId?: number | null;
  /** 'exam' | 'worksheet' | 'homework'. The instant-results warning only applies to exams. */
  kind?: string;
}) {
  const sectionsQuery = useSectionsList(gradeId);
  const taught = useTaughtSubjects();
  // A teacher of some sections only: no whole grade, and only their own sections.
  const mine = taught.sectionsFor(gradeId, subjectId);
  const sections = (sectionsQuery.data?.sections ?? []).filter((s) => mine == null || mine.includes(s.id));
  const mineKey = mine?.join(',') ?? '';
  useEffect(() => {
    if (mine == null) return;
    const kept = value.sectionIds.filter((id) => mine.includes(id));
    if (value.mode === 'grade') onChange({ ...value, mode: 'sections', sectionIds: kept.length ? kept : mine });
    else if (kept.length !== value.sectionIds.length) onChange({ ...value, sectionIds: kept });
  }, [mineKey, value.mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const noSections = sectionsQuery.isSuccess && sections.length === 0;
  // Saved groups (teacher grouping) the work can go to, kept to the sections the viewer gives work to.
  const groupsQuery = useAssignableGroups(gradeId, subjectId);
  const groups = (groupsQuery.data?.groups ?? []).filter((g) => mine == null || mine.includes(g.section.id));
  const sameSet = (a: number[], b: number[]) => a.length === b.length && a.every((id) => b.includes(id));
  const set = (patch: Partial<AssignValue>) => onChange({ ...value, ...patch });

  const toggleSection = (id: number) =>
    set({ sectionIds: value.sectionIds.includes(id) ? value.sectionIds.filter((x) => x !== id) : [...value.sectionIds, id] });
  const toggleStudent = (id: number) =>
    set({ studentIds: value.studentIds.includes(id) ? value.studentIds.filter((x) => x !== id) : [...value.studentIds, id] });

  const pastDue = !!value.dueDate && new Date(localIso(value.dueDate, value.dueTime)) < new Date();

  const who = audienceText(value, sections, gradeLabel);
  const due = dueText(value);
  const summary = `${who} · ${due} · results shown ${releaseText(value.resultsRelease)}.${value.mode === 'sections' ? ' Other sections can still find it in the Store.' : ''}`;

  const audienceErr = errors?.assigned_section_ids ?? errors?.assigned_student_ids;
  const dueErr = errors?.due_at ?? errors?.due_date;

  return (
    <div className="space-y-[18px]">
      <fieldset className="min-w-0">
        <legend className={labelClass}>Audience</legend>
        <Segmented<AssignMode>
          label="Audience"
          options={[
            ...(mine == null ? [{ key: 'grade' as const, label: 'Whole grade' }] : []),
            { key: 'sections', label: mine == null ? 'Some sections' : 'My sections', inert: noSections },
            { key: 'students', label: 'Specific students' },
          ]}
          value={value.mode}
          onChange={(m) => { if (m === 'sections' && noSections) return; set({ mode: m }); }}
          fill
          dense
        />
        {noSections && <p className="mt-1.5 text-xs text-muted">Create sections in Setup first.</p>}
        {value.mode === 'sections' && (
          <div className="mt-3.5">
            <p id="assign-sections-label" className={labelClass}>Sections in {gradeLabel ?? 'this grade'}</p>
            <OverflowRow role="group" label="Sections" rowClassName="gap-2">
              {sections.map((s) => (
                <label key={s.id} className={`${pill(value.sectionIds.includes(s.id))} shrink-0 whitespace-nowrap`}>
                  <input type="checkbox" className="sr-only" aria-label={s.display} checked={value.sectionIds.includes(s.id)} onChange={() => toggleSection(s.id)} />
                  <span aria-hidden>{s.display}</span>
                  <span aria-hidden className="font-mono opacity-75">{s.student_count}</span>
                </label>
              ))}
            </OverflowRow>
          </div>
        )}
        {value.mode === 'students' && (
          <div className="mt-3.5">
            {groups.length > 0 && (
              <div className="mb-3.5">
                <p id="assign-groups-label" className={labelClass}>Saved groups</p>
                <OverflowRow role="group" label="Saved groups" rowClassName="gap-2">
                  {groups.map((g) => {
                    const on = g.member_ids.length > 0 && sameSet(value.studentIds, g.member_ids);
                    return (
                      <button key={g.id} type="button" aria-pressed={on} title={`Section ${g.section.name}`}
                        className={`${pill(on)} shrink-0 whitespace-nowrap`}
                        onClick={() => set({ studentIds: [...g.member_ids] })}>
                        <span>{g.name}</span>
                        <span aria-hidden className="font-mono opacity-75">{g.size}</span>
                      </button>
                    );
                  })}
                </OverflowRow>
              </div>
            )}
            <IndividualStudentPicker gradeId={String(gradeId ?? '')} gradeLabel={gradeLabel} selectedIds={value.studentIds} onToggle={toggleStudent} sectionIds={mine} />
          </div>
        )}
        {audienceErr && <p className={errClass}>{audienceErr}</p>}
      </fieldset>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <label htmlFor="assign-due-date" className={labelClass}>Due date</label>
          <input
            id="assign-due-date"
            type="date"
            className={inputClass}
            value={value.dueDate}
            onChange={(e) => {
              const d = e.target.value;
              if (!d) set({ dueDate: '', dueTime: '' });
              else set({ dueDate: d, dueTime: value.dueTime || '23:59' });
            }}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor="assign-due-time" className={labelClass}>Due time</label>
          <input
            id="assign-due-time"
            type="time"
            className={inputClass}
            value={value.dueTime}
            disabled={!value.dueDate}
            onChange={(e) => set({ dueTime: e.target.value || '23:59' })}
          />
        </div>
      </div>
      {pastDue && <p className="text-xs text-warning-ink">This due time has already passed.</p>}
      {dueErr && <p className={errClass}>{dueErr}</p>}

      <div>
        <label htmlFor="assign-instructions" className={labelClass}>Instructions</label>
        <textarea
          id="assign-instructions"
          className={`${inputClass} min-h-[76px] resize-y leading-normal`}
          maxLength={MAX}
          rows={3}
          value={value.instructions}
          onChange={(e) => set({ instructions: e.target.value })}
        />
        {errors?.instructions && <p className={errClass}>{errors.instructions}</p>}
      </div>

      <fieldset className="min-w-0">
        <legend className={labelClass}>Show results to students</legend>
        <div className="flex flex-wrap gap-2">
          {RELEASE_OPTIONS.map(([val, label]) => (
            <label key={val} className={pill(value.resultsRelease === val)}>
              <input type="radio" className="sr-only" name="assign-results-release" checked={value.resultsRelease === val} onChange={() => set({ resultsRelease: val })} />
              {label}
            </label>
          ))}
        </div>
        {kind === 'exam' && value.resultsRelease === 'immediate' && (
          <p className="mt-2 text-xs text-muted">Students who finish first can share their answers with classmates who haven't started. 'After the due time' is safer.</p>
        )}
      </fieldset>

      <div className="flex gap-2.5 rounded-[14px] bg-ground px-4 py-3.5 text-[13px] text-muted-2">
        <Info size={18} strokeWidth={1.8} aria-hidden className="mt-px flex-none" />
        <p className="min-w-0">{summary}</p>
      </div>
    </div>
  );
}
